/*  Batch-conductor sizing · shared primitives.
    Extracted from BatchConductor.jsx during the v2.87 refactor for
    reuse across the main component, RowExpanded, and future batch
    sub-tools.  Kept intentionally tiny; no state, no side effects.
*/

export const Th = ({ children, className = "" }) => (
  <th className={`text-left font-normal px-2 py-2 text-[10px] uppercase tracking-wider ${className}`}>
    {children}
  </th>
);

export const Td = ({ children, className = "" }) => (
  <td className={`px-1.5 py-1 align-middle ${className}`}>{children}</td>
);

export const CellInput = ({ value, onChange, type = "text", className = "", testid }) => (
  <input
    type={type}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    className={`bg-black/40 border border-white/10 px-1.5 py-1 text-xs font-mono focus:border-[#00E5FF] outline-none ${className}`}
    data-testid={testid}
  />
);

export const CellSelect = ({ value, onChange, options, className = "", testid }) => (
  <select
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    className={`bg-black/40 border border-white/10 px-1 py-1 text-xs font-mono focus:border-[#00E5FF] outline-none ${className}`}
    data-testid={testid}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

export const LabeledInput = ({ label, value, onChange, placeholder, testid }) => (
  <label className="block">
    <span className="label-tag block mb-1">{label}</span>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
      data-testid={testid}
    />
  </label>
);

/* Single-value editor for the bulk-apply toolbar — picks the widget
   based on the column metadata (see BULK_COLUMNS in ./constants). */
export function BulkValueEditor({ column, value, onChange, cableTypes }) {
  if (!column) return null;
  const testid = "batch-bulk-value";
  const cls = "bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono focus:border-[#00E5FF] outline-none";
  if (column.type === "boolean") {
    return (
      <label className="inline-flex items-center gap-1 text-xs font-mono text-gray-300">
        <input type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={testid} />
        <span>enable</span>
      </label>
    );
  }
  if (column.type === "cable") {
    const opts = (cableTypes.length ? cableTypes : [{ key: "THWN-2" }])
      .map((c) => (typeof c === "string" ? c : (c.key || c.id)));
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className={cls} data-testid={testid}>
        {opts.map((k) => <option key={k} value={k}>{k}</option>)}
      </select>
    );
  }
  if (column.type === "select") {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className={cls} data-testid={testid}>
        {column.options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }
  return (
    <input type={column.type === "number" ? "number" : "text"}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className={`${cls} w-24`}
      data-testid={testid}
      placeholder={column.label} />
  );
}

/* Per-row Result cell — collapses ok/err/mode-specific OCPD into a
   compact 3-line display. */
export function ResultCell({ res, hasComputed, testid }) {
  if (!res) {
    return (
      <div className="text-[10px] text-gray-600" data-testid={testid}>
        {hasComputed ? "— input changed —" : "not computed"}
      </div>
    );
  }
  if (!res.ok) {
    return (
      <div className="text-[10px] text-[#FF8A85] leading-tight" data-testid={testid}>
        <strong>ERR:</strong> {res.error?.slice(0, 80) || "compute failed"}
      </div>
    );
  }
  const r = res.result || {};
  const mb = r.motor_branch;
  const tx = r.transformer;
  const mg = r.motor_group?.supported ? r.motor_group : null;
  // v2.87 · When transformer both-sides is on, show both cables inline.
  const bothSides = tx?.size_both_sides && tx?.primary_cable && tx?.secondary_cable;
  if (bothSides) {
    const pri = tx.primary_cable;
    const sec = tx.secondary_cable;
    return (
      <div className="leading-tight" data-testid={testid}>
        <div className="text-[10px]">
          <span className="text-[#00E5FF]">P: {pri.selected_size}</span>
          <span className="text-gray-500"> · {pri.derated_ampacity}A · </span>
          <span className="text-[#00E5FF]">{pri.recommended_ocpd_amps}A</span>
        </div>
        <div className="text-[10px]">
          <span className="text-[#FFCC00]">S: {sec.selected_size}</span>
          <span className="text-gray-500"> · {sec.derated_ampacity}A · </span>
          <span className="text-[#FFCC00]">{sec.recommended_ocpd_amps}A</span>
        </div>
        <div className="text-[9px] text-gray-500">VD P {pri.voltage_drop_pct}% · S {sec.voltage_drop_pct}%</div>
      </div>
    );
  }
  return (
    <div className="leading-tight" data-testid={testid}>
      <div className="text-[#00E5FF] text-[11px] font-bold">
        {r.selected_size} <span className="text-gray-400 font-normal">AWG/kcmil</span>
      </div>
      <div className="text-[10px] text-gray-400">
        {r.derated_ampacity}A · VD {r.voltage_drop_pct}%
        {!r.voltage_drop_compliant && <span className="text-[#FF8A85]"> ⚠</span>}
      </div>
      <div className="text-[10px] text-gray-500">
        {mg ? `Feeder OCPD ${mg.feeder_ocpd}A`
          : mb ? `Motor OCPD ${mb.recommended_ocpd_amps}A`
          : tx ? `TX OCPD ${(tx.secondary?.recommended_ocpd_amps ?? tx.primary.recommended_ocpd_amps)}A`
          : `Breaker ${r.recommended_breaker}A`}
      </div>
    </div>
  );
}
